<template>
  <div>
    <DrawingRequestShowState
      v-if="loading || errorStatus"
      :loading="loading"
      :error-status="errorStatus"
      :request-id="id"
      @retry="fetchDrawingRequest"
    />

    <template v-else>
      <DrawingRequestShowHeader :drawing-request="drawingRequest" :back-link="backLink" />

      <b-container fluid class="pt-3 px-3 px-md-4 mb-1">
        <b-row>
          <b-col lg="8" class="mb-2 mb-md-0">
            <DrawingRequestShowChat
              :comments="drawingRequest.comments"
              :audit-events="drawingRequest.audit_events"
              :loading-attachment-id="loadingAttachmentId"
              @open-attachment="openAttachment"
            >
              <template #composer>
                <DrawingRequestShowCommentForm
                  v-if="permissions.can_comment"
                  :drawing-request-id="drawingRequest.id"
                  :current-status="drawingRequest.status"
                  :permissions="permissions"
                  @created="onCommentCreated"
                />
                <DrawingRequestShowApprove
                  v-else-if="drawingRequest.status === 'revision'"
                  :drawing-request-id="drawingRequest.id"
                  :can-approve="!!permissions.can_approve"
                  @decided="onCommentCreated"
                  @stale="fetchDrawingRequest"
                />
                <DrawingRequestShowReopenBar
                  v-else-if="permissions.can_reopen"
                  :drawing-request-id="drawingRequest.id"
                  :status="drawingRequest.status"
                  @reopened="onCommentCreated"
                  @stale="fetchDrawingRequest"
                />
              </template>
            </DrawingRequestShowChat>
          </b-col>

          <b-col lg="4">
            <div class="sticky-top side-card" style="top: 1rem; z-index: 1">
              <DrawingRequestShowDetails
                :drawing-request="drawingRequest"
                :permissions="permissions"
                @edit="openEditModal"
              />

              <DrawingRequestShowTabs
                :drawing-request="drawingRequest"
                :permissions="permissions"
                :loading-attachment-id="loadingAttachmentId"
                @updated="onDrawingRequestEdited"
                @open-attachment="openAttachment"
                @item-added="onItemAdded"
                @item-removed="onItemRemoved"
              />
            </div>
          </b-col>
        </b-row>
      </b-container>
    </template>

    <!-- Modals -->
    <DrawingRequestEditModal
      v-if="editModalShow && permissions.can_edit_details"
      :drawing-request="drawingRequest"
      :permissions="permissions"
      @close="editModalShow = false"
      @updated="onDrawingRequestEdited"
    />

    <ImageFullModal
      v-if="fullImageUrl"
      :image-url="fullImageUrl"
      :disableControls="true"
      @close-modal="fullImageUrl = null"
    />
  </div>
</template>

<script>
import DrawingRequestShowHeader from './DrawingRequestShowHeader'
import DrawingRequestShowDetails from './DrawingRequestShowDetails.vue';
import DrawingRequestEditModal from './DrawingRequestEditModal'
import DrawingRequestShowTabs from './DrawingRequestShowTabs'
import DrawingRequestShowChat from './DrawingRequestShowChat'
import DrawingRequestShowCommentForm from './DrawingRequestShowCommentForm'
import DrawingRequestShowState from './DrawingRequestShowState'
import DrawingRequestShowReopenBar from './DrawingRequestShowReopenBar'
import DrawingRequestShowApprove from './DrawingRequestShowApprove'
import ImageFullModal from '../../shared/ImageFullModal'

export default {
  name: 'DrawingRequestShow',
  components: {
    DrawingRequestShowHeader,
    DrawingRequestShowDetails,
    DrawingRequestEditModal,
    DrawingRequestShowTabs,
    DrawingRequestShowChat,
    DrawingRequestShowCommentForm,
    DrawingRequestShowState,
    DrawingRequestShowReopenBar,
    DrawingRequestShowApprove,
    ImageFullModal
  },
  data () {
    return {
      id: Number(this.$route.params.id),
      drawingRequest: null,
      fromRoute: null,
      loading: true,
      errorStatus: null,
      editModalShow: false,
      fullImageUrl: null,
      loadingAttachmentId: null
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => { vm.fromRoute = from })
  },
  computed: {
    backLink () {
      if (this.fromRoute && this.fromRoute.name === 'DrawingRequestIndex') {
        return { to: this.fromRoute.fullPath, label: 'Solicitudes' }
      }
      return { to: `/app/prod/proyectos/${this.drawingRequest.project.id}`, label: 'Proyecto' }
    },
    permissions () {
      return (this.drawingRequest && this.drawingRequest.permissions) || {}
    }
  },
  watch: {
    '$route.params.id' (value) {
      this.id = Number(value)
      this.fetchDrawingRequest()
    }
  },
  mounted () {
    this.fetchDrawingRequest()
  },
  methods: {
    fetchDrawingRequest () {
      this.loading = true
      this.errorStatus = null

      this.$http.get(`/api/production/drawing_requests/${this.id}`)
        .then((res) => {
          this.drawingRequest = res.data
        })
        .catch((error) => {
          this.errorStatus = (error.response && error.response.status) || 'network'
        })
        .finally(() => {
          this.loading = false
        })
    },
    openEditModal () {
      this.editModalShow = true
    },
    onDrawingRequestEdited ({ drawing_request: updated, audit_events: auditEvents, permissions }) {
      this.drawingRequest = {
        ...this.drawingRequest,
        ...updated,
        audit_events: [...this.drawingRequest.audit_events, ...auditEvents],
        permissions
      }
      this.editModalShow = false
    },
    // comments
    onCommentCreated ({ comment, audit_event: auditEvent, drawing_request: updated, permissions }) {
      this.drawingRequest.comments.push(comment)
      if (auditEvent) this.drawingRequest.audit_events.push(auditEvent)
      this.drawingRequest.status = updated.status
      this.drawingRequest.status_label = updated.status_label
      if (permissions) this.drawingRequest.permissions = permissions
    },
    openAttachment (comment) {
      if (this.loadingAttachmentId) return
      this.loadingAttachmentId = comment.id

      const baseUrl = `/api/production/drawing_requests/${this.drawingRequest.id}/comments/${comment.id}`

      if (comment.photo && comment.photo.id) {
        this.$http.get(`${baseUrl}/photo`)
          .then((res) => {
            this.fullImageUrl = res.data.presigned_url
          })
          .catch(e => console.log(e))
          .finally(() => { this.loadingAttachmentId = null })
      } else {
        this.$http.get(`${baseUrl}/document`)
          .then((res) => {
            const link = document.createElement('a')
            link.href = res.data
            link.download = comment.document.name
            document.body.appendChild(link)
            link.click()
            link.remove()
          })
          .catch(e => console.log(e))
          .finally(() => { this.loadingAttachmentId = null })
      }
    },
    // submitals
    onItemAdded ({ item, audit_event: auditEvent }) {
      this.drawingRequest.drawing_request_items.push(item)
      this.drawingRequest.audit_events.push(auditEvent)
    },
    onItemRemoved ({ id, audit_event: auditEvent }) {
      this.drawingRequest.drawing_request_items = this.drawingRequest.drawing_request_items.filter(item => item.id !== id)
      this.drawingRequest.audit_events.push(auditEvent)
    }
  }
}
</script>
